import FilePickerCommandBar from './FilePickerCommandBar';
import FilePickerPane from './FilePickerPane';
import FileProvidersPane from './FileProvidersPane';
import changeFilterOptionAndReloadItemsForSelectedProvider from '../actions/changeFilterOptionAndReloadItemsForSelectedProvider';
import closeFilePicker from '../actions/closeFilePicker';
import setShowFilterOptions from '../actions/setShowFilterOptions';
import showFilePickerAction from '../actions/showFilePicker';
import toggleFilePickerView from '../actions/toggleFilePickerView';
import { updateSupportedFilePickerActionsForProviderFiles } from '../actions/updateSupportedFilePickerActions';
import actionsPanel from '../actionsPanel';
import { getFilePickerStore } from '../store/filePickerStore';
import FilePickerAction from '../store/schema/FilePickerAction';
import FilePickerConfiguration from '../store/schema/FilePickerConfiguration';
import FilePickerPanelViewState from '../store/schema/FilePickerPanelViewState';
import FileProviderItemViewState from '../store/schema/FileProviderItemViewState';
import FilePickerResult, { SelectedFilesSource } from '../types/FilePickerResult';
import { getFileProviderInfo } from '../utils/fileProviderInfo';
import { observer } from 'mobx-react';
import { Modal } from 'office-ui-fabric-react/lib/Modal';
import { logUsage } from 'owa-analytics';
import { logMountingPerformance } from 'owa-analytics';
import { isFeatureEnabled } from 'owa-feature-flags';
import { FilePickerUserConfiguration } from 'owa-filepicker';
import { FilePickerViewType } from 'owa-filepicker';
import { ClientOnlyFileProviderType, FileSelectionFilter } from 'owa-fileprovider-store';
import { getFileProviders } from 'owa-fileprovider-store/lib/selectors/getFileProviders';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import AttachmentItemsFilterBy from 'owa-service/lib/contract/AttachmentItemsFilterBy';
import * as React from 'react';
import * as ReactDOM from 'react-dom';

interface FilePickerPanelProps {
    onFilesSelectedCallback: (result: FilePickerResult) => void;
    onCancelCallback: () => void;
    viewState: FilePickerPanelViewState;
    disableLocalComputerFilePicker?: boolean;
    maxAllowedLocalFilesSize?: number;
    userConfiguration?: FilePickerUserConfiguration;
}

const styles = require('./FilePickerPanelView.scss');
const classNames = require('classnames');

/* exported for testing */
export const FILEPICKER_DIV_ID = 'filePickerPanelView';
// This component is responsible for showing the attachment file picker including inline images.
@observer
@logMountingPerformance('FilePickerPanelView')
class FilePickerPanelView extends React.Component<FilePickerPanelProps, {}> {
    private filePickerResult: FilePickerResult = null;

    render() {
        return (
            <Modal
                isOpen={this.props.viewState.isOpen}
                onDismiss={this.closeFilePickerPanel}
                onDismissed={this.onPanelDismissed}>
                <div className={styles.modalContainer}>{this.renderFilePickerContent()}</div>
            </Modal>
        );
    }

    private renderFilePickerContent = () => {
        const filterOptionsState = this.props.viewState.filePickerViewState.filterOptionsState;
        let providerFilterOptions: AttachmentItemsFilterBy[] = [];
        if (this.props.viewState.selectedFileProvider != null) {
            providerFilterOptions = getFileProviderInfo(
                this.props.viewState.selectedFileProvider.type
            ).filePickerFilterOptions;
        }

        const { supportedViews, currentView } = this.getSupportedAndCurrentViews();

        return (
            <div className={styles.filePickerContent}>
                <FilePickerCommandBar
                    toggleSelectedFilePickerView={this.onFilePickerViewChanged}
                    onCloseFilePickerClicked={this.closeFilePickerPanel}
                    selectedFilePickerView={currentView}
                    supportedFilePickerViews={supportedViews}
                    filterOptions={providerFilterOptions}
                    selectedFilterOption={filterOptionsState.selectedFilterOption}
                    showFilterOptionsMenu={filterOptionsState.showFilterOptions}
                    onFilterIconClicked={this.onFilterClicked}
                    onFilterOptionClicked={this.onFilterOptionClicked}
                    onFilterMenuDismissed={this.onFilterMenuDismissed}
                />
                <div className={styles.content}>
                    <FileProvidersPane
                        className={classNames(styles.fileProvidersPane)}
                        fileSelectionFilter={this.props.viewState.fileSelectionFilter}
                        onLocalComputerFilesSelected={this.onLocalComputerFilesSelected}
                        filePickerPanelViewState={this.props.viewState}
                        selectedFileProvider={this.props.viewState.selectedFileProvider}
                        disableLocalComputerFilePicker={this.props.disableLocalComputerFilePicker}
                    />
                    <FilePickerPane
                        className={classNames(styles.filePickerPane)}
                        viewState={this.props.viewState}
                        onFilesSelected={this.onProviderFilesSelected}
                        maxAllowedLocalFilesSize={this.props.maxAllowedLocalFilesSize}
                        selectedView={currentView}
                        isTopLevelGroupsView={this.isTopLevelGroupsView()}
                    />
                </div>
            </div>
        );
    };

    private getSupportedAndCurrentViews = (): {
        supportedViews: FilePickerViewType[];
        currentView: FilePickerViewType;
    } => {
        if (isFeatureEnabled('doc-filePicker-groupFiles') && this.isTopLevelGroupsView()) {
            return {
                supportedViews: [FilePickerViewType.Groups],
                currentView: FilePickerViewType.Groups,
            };
        } else {
            return {
                supportedViews: this.props.viewState.filePickerViewState.supportedViews,
                currentView: this.props.viewState.filePickerViewState.selectedView,
            };
        }
    };

    private isTopLevelGroupsView() {
        return (
            this.props.viewState.selectedFileProvider &&
            this.props.viewState.selectedFileProvider.usesGroupsViewAtTopLevel &&
            this.props.viewState.filePickerViewState.selectedFolder === null
        );
    }

    private onProviderFilesSelected = (files: FileProviderItemViewState[]) => {
        if (this.props.viewState.filePickerViewState.searchViewState.isInSearchMode) {
            logUsage('FPSelectedFromSearch', [this.props.viewState.selectedFileProvider.type]);
        }

        this.filePickerResult = {
            files: files,
            source: SelectedFilesSource.ProviderFiles,
            action: null,
        };

        // If the data provider does not allow showing the action panel then we default to attach as classic attachment
        if (!getFileProviderInfo(this.props.viewState.selectedFileProvider.type).showActionsPanel) {
            this.executeFileProviderDefaultAction(this.props.viewState.selectedFileProvider.type);
            return;
        }

        this.showActionsPanel(files, SelectedFilesSource.ProviderFiles);
    };

    private executeFileProviderDefaultAction = (dataProviderType: AttachmentDataProviderType) => {
        switch (dataProviderType) {
            case AttachmentDataProviderType.Mailbox:
                this.onActionSelected(FilePickerAction.AttachAndShare);
                break;
            case AttachmentDataProviderType.Facebook:
                this.onActionSelected(FilePickerAction.Attach);
                break;
            case AttachmentDataProviderType.MailMessage:
                this.onActionSelected(FilePickerAction.Attach);
                break;
            default:
                throw new Error(
                    'This provider does not have a current default action assigned to it.'
                );
        }
    };

    private onLocalComputerFilesSelected = (files: File[], filter: FileSelectionFilter) => {
        if (!files || files.length === 0) {
            return;
        }

        this.filePickerResult = {
            files: files,
            source: SelectedFilesSource.LocalComputerFiles,
            action: null,
        };

        this.showActionsPanel(files, SelectedFilesSource.LocalComputerFiles);
    };

    private onFilePickerViewChanged = () => {
        const {
            viewState: { filePickerViewState, selectedFileProvider },
        } = this.props;
        const onViewTypeChanged =
            this.props.userConfiguration && this.props.userConfiguration.preferredViewType
                ? this.props.userConfiguration.preferredViewType.valueSetter
                : null;

        toggleFilePickerView(selectedFileProvider, filePickerViewState, onViewTypeChanged);
    };

    private showActionsPanel = (
        files: File[] | FileProviderItemViewState[],
        selectedFileSource: SelectedFilesSource
    ) => {
        const { fileSelectionFilter, selectedFileProvider } = this.props.viewState;
        const fileProvidersViewState = getFileProviders();

        if (fileSelectionFilter === FileSelectionFilter.Images) {
            // If we already know this is the inline image picker we don't need to show the actions panel
            this.onActionSelected(FilePickerAction.Insert);
            return;
        }

        if (fileProvidersViewState.length === 0) {
            // If we have not loaded the current file providers then skip actions panel and
            // directly attach
            this.onActionSelected(FilePickerAction.Attach);
            return;
        }

        const isGroupsFileProvider: boolean =
            this.props.viewState.selectedFileProvider &&
            this.props.viewState.selectedFileProvider.viewType ===
                ClientOnlyFileProviderType.Groups;

        actionsPanel(
            files,
            this.props.maxAllowedLocalFilesSize,
            selectedFileSource === SelectedFilesSource.ProviderFiles
                ? selectedFileProvider.type
                : null,
            isGroupsFileProvider,
            files.length > 1,
            selectedFileSource,
            this.props.userConfiguration,
            { filePickerPanelViewState: this.props.viewState }
        ).then(this.onActionSelected, this.onActionPanelCancelled);
    };

    private onActionPanelCancelled = () => {
        this.resetSupportedActionsForSelectedProviderFiles();
        this.filePickerResult = null;
    };

    private resetSupportedActionsForSelectedProviderFiles = () => {
        const { filePickerViewState, selectedFileProvider } = this.props.viewState;
        updateSupportedFilePickerActionsForProviderFiles(
            filePickerViewState.selectedFileProviderItems,
            this.props.maxAllowedLocalFilesSize,
            selectedFileProvider,
            filePickerViewState
        );
    };

    private onActionSelected = (action: FilePickerAction) => {
        this.filePickerResult.action = action;
        this.closeFilePickerPanel();
    };

    private closeFilePickerPanel = () => {
        closeFilePicker(this.props.maxAllowedLocalFilesSize, this.props.viewState);
    };

    private onPanelDismissed = () => {
        // We need to make the callbacks only when the animation is complete so the focus can be changed back
        if (!this.filePickerResult) {
            logUsage('FPDismissProviderType', [
                this.props.viewState.selectedFileProvider.model.Type,
            ]);

            this.props.onCancelCallback();
        }

        this.props.onFilesSelectedCallback(this.filePickerResult);
        this.filePickerResult = null;
    };

    private onFilterClicked = () => {
        setShowFilterOptions(
            this.props.viewState.filePickerViewState.filterOptionsState,
            !this.props.viewState.filePickerViewState.filterOptionsState.showFilterOptions
        );
    };

    private onFilterMenuDismissed = () => {
        setShowFilterOptions(this.props.viewState.filePickerViewState.filterOptionsState, false);
    };

    private onFilterOptionClicked = (selectedFilter: AttachmentItemsFilterBy) => {
        changeFilterOptionAndReloadItemsForSelectedProvider(this.props.viewState, selectedFilter);
    };
}

/**
 * The showFilePicker is responsible for creating an element and then rendering the FilePicker in it.
 * The reason to do that is to make the file picker component simpler from a user perspective so that
 * they don't have to worry about creating the component themselves and maintaining show/close lifecycle.
 * Instead they create the file picker and once user selects files they get the files back.
 */
export default function showFilePickerPanel(
    resolve: (result: FilePickerResult) => void,
    reject: () => void,
    filePickerConfiguration: FilePickerConfiguration
): void {
    const {
        userConfiguration,
        fileSelectionFilter,
        uploadConfiguration,
        maxAllowedLocalFilesSize,
        disableLocalComputerFilePicker,
    } = filePickerConfiguration;
    let filePickerDiv = document.getElementById(FILEPICKER_DIV_ID);
    if (!filePickerDiv) {
        // As we can have only one file picker open at a time so we only create the
        // DOM element once and reuse that.
        filePickerDiv = document.createElement('div');
        filePickerDiv.id = FILEPICKER_DIV_ID;
        document.body.appendChild(filePickerDiv);
    }

    ReactDOM.render(
        <FilePickerPanelView
            onFilesSelectedCallback={resolve}
            onCancelCallback={reject}
            viewState={getFilePickerStore().viewState}
            disableLocalComputerFilePicker={disableLocalComputerFilePicker}
            maxAllowedLocalFilesSize={maxAllowedLocalFilesSize}
            userConfiguration={userConfiguration}
        />,
        filePickerDiv
    );

    const viewType = userConfiguration !== null ? userConfiguration.preferredViewType.value : null;
    showFilePickerAction(
        fileSelectionFilter,
        viewType,
        getFilePickerStore().viewState,
        uploadConfiguration
    );
}
